import type { UiMode } from '@/composables/useUiMode'

/** One depth as the API sends it: the stored key and the most tokens a message may carry at that depth. */
export interface ChatMemoryDepthOption {
    depth: string
    maxTokens: number
}

/**
 * The names people read for the stored depth keys, shallowest to deepest (#1764, CTO decision 1). Only the portal
 * renames them: the API, the shared constants and the stored values keep `standard`, `large` and `extended`.
 * The middle level is Balanced, not Standard, because Standard already names the portal-wide Standard/Advanced mode
 * (`useUiMode`, shown on the Account page) and one word must not mean two things on one screen (CEO, PR #2198).
 */
const DEPTH_NAMES: ReadonlyMap<string, string> = new Map([
    ['standard', 'Light'],
    ['large', 'Balanced'],
    ['extended', 'Deep'],
])

/** A whole-number ratio in words, from twice to ten times. */
const RATIO_WORDS: ReadonlyMap<number, string> = new Map([
    [2, 'twice'],
    [3, 'three times'],
    [4, 'four times'],
    [5, 'five times'],
    [6, 'six times'],
    [7, 'seven times'],
    [8, 'eight times'],
    [9, 'nine times'],
    [10, 'ten times'],
])

/** The name the picker shows for a depth: the portal's name for it, or its raw key when the portal has none. */
function depthName(depth: string): string {
    return DEPTH_NAMES.get(depth) ?? depth
}

/** The depth with the lowest cap, which every cost note is measured against. The first one wins a tie. */
function lightestOf(option: ChatMemoryDepthOption, depths: readonly ChatMemoryDepthOption[]): ChatMemoryDepthOption {
    return depths.reduce(
        (lightest, each) => (each.maxTokens < lightest.maxTokens ? each : lightest),
        depths[0] ?? option,
    )
}

/**
 * What a depth costs next to the lightest one, in words. The ratio of the caps is rounded to a whole number and
 * spelled out up to ten; above that it reads "more than ten times". A cap that rounds to the lightest one's is about
 * the same cost, still said in words.
 */
function costNote(option: ChatMemoryDepthOption, lightest: ChatMemoryDepthOption): string {
    if (option === lightest || option.maxTokens <= lightest.maxTokens) {
        return 'lowest cost'
    }
    const against = depthName(lightest.depth)
    const ratio = Math.round(option.maxTokens / lightest.maxTokens)
    if (ratio <= 1) {
        return `about the same memory cost as ${against}`
    }
    const words = RATIO_WORDS.get(ratio) ?? 'more than ten times'
    return `up to ${words} the memory cost of ${against}`
}

/**
 * How the Chat memory picker words one depth (#1764): its name and a cost note in words worked out from the caps, so
 * the note stays true if a cap changes. Advanced mode adds the exact cap after the words and never drops them.
 */
export function chatMemoryDepthLabel(
    option: ChatMemoryDepthOption,
    depths: readonly ChatMemoryDepthOption[],
    mode: UiMode,
): string {
    const words = `${depthName(option.depth)} · ${costNote(option, lightestOf(option, depths))}`
    if (mode !== 'advanced') {
        return words
    }
    return `${words} · up to ${option.maxTokens.toLocaleString('en-US')} tokens a message`
}

/**
 * What the closed picker shows for the depth in force: the same text as its option, so the trigger follows a mode
 * switch with no reload. A depth the API did not list among its depths has no cap to compare, so it shows its name.
 */
export function chatMemoryChosenLabel(
    setting: { chatMemoryDepth: string; depths: readonly ChatMemoryDepthOption[] },
    mode: UiMode,
): string {
    const chosen = setting.depths.find(option => option.depth === setting.chatMemoryDepth)
    return chosen ? chatMemoryDepthLabel(chosen, setting.depths, mode) : depthName(setting.chatMemoryDepth)
}
